import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { ChevronRight } from "lucide-react";
import Loader from "../../../components/Loader/Loader";
import PageShell from "../../../components/layout/PageShell";
import PageHead from "../../../components/layout/PageHead";
import Alert from "../../../components/v3/Alert";
import Empty from "../../../components/v3/Empty";
import Field from "../../../components/v3/Field";
import Btn from "../../../components/v3/Btn";
import { formatAuditTimestamp } from "../../../utils/fmtDate";
import { maskAccount } from "../../../utils/bankMask";
import { verificationLabel } from "../../../utils/orderAccountKind";

/**
 * BankAccountQueue - company bank accounts waiting on compliance (corporate
 * Business PAD contract, Amendment A1).
 *
 * Compliance verifies a company bank account before the first debit (I4).
 * With feature.canonical_admin_login armed the legacy user pages are AD-only,
 * and every compliance seat (DR, CCO, UDP) is held by a non-AD account, so
 * the verification lives here, on the compliance surface. The queue is GET
 * admin/investor-payment-accounts/review-queue?status=PENDING|UNVERIFIED|REJECTED:
 * the active ENTITY versions in that status, oldest first (sorted here too,
 * whatever order the answer arrives in). Opening a row opens that user's
 * accounts on the review page (BankAccountReview); nothing here reads a
 * user's profile, and nothing here decides.
 *
 * The route may not be deployed yet: a 404, a 403 or a failed read is a line
 * in words, and a user id box opens a user's accounts directly.
 */

const STATUS_OPTIONS = [
  { value: "PENDING", label: "Pending review" },
  { value: "UNVERIFIED", label: "Unverified" },
  { value: "REJECTED", label: "Rejected" },
];

const USER_ID = /^[1-9]\d*$/;
const USER_ID_HINT = "Enter a user id: a whole number above zero.";

function authHeaders() {
  return { "x-access-token": localStorage.getItem("accessToken") };
}

/** The queue as a list: a bare array, or { data: [...] }. */
function rowsOf(body) {
  if (Array.isArray(body)) return body;
  if (body && Array.isArray(body.data)) return body.data;
  return [];
}

/** Oldest first; a row with no readable date goes last. */
function oldestFirst(a, b) {
  const ta = Date.parse(a && a.createdAt);
  const tb = Date.parse(b && b.createdAt);
  if (Number.isNaN(ta)) return Number.isNaN(tb) ? 0 : 1;
  if (Number.isNaN(tb)) return -1;
  return ta - tb;
}

function queueFailureText(err) {
  const status = err?.response?.status;
  if (status === 404) return "The review queue is not available yet.";
  if (status === 403) return "The review queue is not visible to your role.";
  return "The review queue could not be loaded.";
}

const accountsPath = (userId) => `/compliance/bank-accounts/${userId}`;
const openableId = (userId) => (USER_ID.test(String(userId ?? "")) ? String(userId) : null);

/** Opens one user's accounts by id, when the queue cannot list them. */
const UserIdBox = ({ onOpen }) => {
  const [value, setValue] = useState("");
  const [invalid, setInvalid] = useState(false);
  const submit = (e) => {
    e.preventDefault();
    const id = value.trim();
    if (!USER_ID.test(id)) {
      setInvalid(true);
      return;
    }
    setInvalid(false);
    onOpen(id);
  };
  return (
    <form className="toolbar" onSubmit={submit}>
      <Field
        label="User id"
        name="bank-accounts-user-id"
        inputMode="numeric"
        value={value}
        onChange={(e) => setValue(e.target.value)}
        error={invalid ? USER_ID_HINT : undefined}
      />
      <Btn type="submit" size="sm">Open accounts</Btn>
    </form>
  );
};

function BankAccountQueue() {
  const navigate = useNavigate();
  const [status, setStatus] = useState("PENDING");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [failure, setFailure] = useState(null);

  // Every filter change starts a request and none of them cancel: a slow
  // answer for an earlier filter must not replace the current one, and an
  // answer that lands after the page is left is dropped unread (review round
  // 3, 2026-10-25: the effect had no cleanup, so a late answer set state on
  // an unmounted page). The effect's cleanup invalidates its request.
  const requestRef = useRef(0);

  useEffect(() => {
    const token = ++requestRef.current;
    const current = () => requestRef.current === token;
    setLoading(true);
    axios
      .get(`${process.env.REACT_APP_API}admin/investor-payment-accounts/review-queue`, {
        headers: authHeaders(),
        params: { status },
      })
      .then((res) => {
        if (!current()) return;
        setRows([...rowsOf(res?.data)].sort(oldestFirst));
        setFailure(null);
      })
      .catch((err) => {
        if (!current()) return;
        setRows([]);
        setFailure(queueFailureText(err));
      })
      .finally(() => {
        if (current()) setLoading(false);
      });
    return () => {
      if (current()) requestRef.current += 1;
    };
  }, [status]);

  const open = (userId) => navigate(accountsPath(userId));

  return (
    <PageShell>
      <Loader isLoading={loading} />
      <PageHead
        title="Company bank accounts"
        subtitle={<span>Company accounts are verified against their void cheque or bank letter before the first debit.</span>}
      />

      <section className="card">
        <div className="card__body">
          <div className="form-grid form-grid--2col">
            <Field
              as="select"
              label="Status"
              name="bank-accounts-status"
              value={status}
              onChange={(e) => setStatus(e.target.value)}
            >
              {STATUS_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </Field>
          </div>
        </div>
      </section>

      {failure ? (
        <section className="card">
          <div className="card__body">
            <Alert tone="warn">{failure}</Alert>
            <p className="muted">Open a user's bank accounts by their user id instead.</p>
            <UserIdBox onOpen={open} />
          </div>
        </section>
      ) : !loading && rows.length === 0 ? (
        <Empty>No company bank account is in this status.</Empty>
      ) : (
        <section className="card">
          <div className="card__body card__body--flush">
            <table className="tbl">
              <thead>
                <tr>
                  <th>Legal entity</th>
                  <th>User</th>
                  <th>Account</th>
                  <th>Status</th>
                  <th>Proof</th>
                  <th>Created</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => {
                  const userId = openableId(row.userId);
                  return (
                    <tr key={row.versionId ?? `${row.userId}-${row.createdAt}`}>
                      <td>{row.legalEntityName || "-"}</td>
                      <td className="mono">{row.userId !== null && row.userId !== undefined ? `#${row.userId}` : "-"}</td>
                      <td className="mono">{maskAccount(row.accountLast4) || "-"}</td>
                      <td>{verificationLabel(row.verificationStatus)}</td>
                      <td className="muted">{row.hasProof === true || row.hasProof === 1 ? "On file" : "None"}</td>
                      <td className="muted">{row.createdAt ? formatAuditTimestamp(row.createdAt) : "-"}</td>
                      <td className="r">
                        {userId && (
                          <Btn variant="ghost" size="sm" onClick={() => open(userId)}>
                            Open <ChevronRight />
                          </Btn>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </section>
      )}
    </PageShell>
  );
}

export default BankAccountQueue;
